/**
 * AddLinkedDbDialog
 *
 * Adds a new linked database (Stage, Prod, …) to an existing Database Blueprint
 * folder. The new link starts with `lastSyncAt = null` — users must run
 * "Refresh from DB" once to populate the baseline.
 *
 * Finding #9: engines mismatch with the blueprint's existing links must be
 *   blocked — every link shares the same `Tables/<schema>.<table>.sql`
 *   filenames, so a pgsql link in an mssql blueprint would silently clobber.
 *
 * Finding #23: the database field is a populated `<Select>` (not a free-text
 *   `<Input>`) once a connection is chosen — typing a non-existent DB name
 *   surfaces only at refresh time and persists a broken link to disk
 *   meanwhile.
 */

import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { getDatabaseFromConnectionId } from '../../../connections/utils/getDatabaseFromConnectionId';
import type { ActiveConnection } from '../../../connections/models/connectionTypes';
import type { Connection } from '../../../../types/electron';
import { addBlueprintLink } from '../../../database-blueprint/services/databaseBlueprintApi';
import { existsByLabel } from '../../../database-blueprint/utils/labelMatch';
import { useScriptBrowserStore } from '../../../script-browser/store/scriptBrowserStore';
import type { MountedFolder } from '../../../script-browser/types/scriptBrowserTypes';
import type { UserFacingEngineId } from '../../../database-blueprint/types/databaseBlueprintTypes';
import { useDatabaseList } from '../../../database-objects/hooks/useDatabaseList';
import { FallbackSelectContent } from '../../../connection-diagnostics/components/FallbackSelectContent';
import { safeConnectionName } from '@/types/safeConnectionDisplay';

type AddLinkedDbDialogProps = {
  open: boolean;
  folder: MountedFolder;
  onOpenChange: (open: boolean) => void;
};

export function inferEngine(engine: string | undefined): UserFacingEngineId {
  if (engine === 'pgsql' || engine === 'postgres') return 'pgsql';
  if (engine === 'mysql') return 'mysql';
  if (engine === 'oracle') return 'oracle';
  if (engine === 'sqlite') return 'sqlite';
  return 'mssql';
}

/**
 * Finding #9 selector — exported for unit tests so the contract is pinned
 * outside the React tree.
 *
 *  - When the blueprint has no existing links, anything is permitted.
 *  - When it has 1+ links, only the first link's engine is allowed.
 *
 * Both sides are normalised through `inferEngine` so the mock test fixture
 * ('mock' → 'mssql') still matches itself.
 */
export function canSubmitForEngineMismatch(
  existing: ReadonlyArray<{ engine: string }>,
  picked: string,
): boolean {
  if (existing.length === 0) return true;
  return inferEngine(existing[0].engine) === inferEngine(picked);
}

/**
 * Derives whether the picked connection is actively connected.
 * Exported so the contract can be pinned by unit tests.
 */
export function isConnectionActive(
  connectionId: string | undefined,
  activeConnections: ReadonlyArray<ActiveConnection>,
): boolean {
  if (!connectionId) return false;
  return activeConnections.some(
    (ac) => ac.id === connectionId && ac.status === 'connected',
  );
}

export function AddLinkedDbDialog({ open, folder, onOpenChange }: AddLinkedDbDialogProps) {
  // Finding #12 — narrow selector so the dialog doesn't re-render on every
  // connection-store mutation (status ticks, query metadata refreshes, etc.).
  // `useShallow` lets us return a fresh tuple object while keeping per-field
  // equality checks against the previous render's values.
  const { connections, activeConnections } = useConnectionStore(
    useShallow((s) => ({
      connections: s.connections,
      activeConnections: s.activeConnections,
    })),
  );
  const connectToDatabase = useConnectionStore((s) => s.connectToDatabase);
  const updateBlueprintMetadata = useScriptBrowserStore((s) => s.updateBlueprintMetadata);
  const [label, setLabel] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [database, setDatabase] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);

  // Soporte-4 closeout follow-up #12 — de-dupe by id, saved record wins.
  // A connection that is both saved AND currently connected used to appear
  // twice (identical label, identical option `data-testid`) because this
  // list concatenated the saved and connected arrays without checking for
  // overlap. Decision: when the same id is in both lists, keep only the
  // saved record — the row then reads the same whether or not you happen to
  // be connected, which is the property that matters in a picker you use
  // once per linked database. A connection that is connected but NOT saved
  // (ad-hoc) has no id collision to resolve and still passes through.
  const allConnections = useMemo(() => {
    const savedIds = new Set(connections.map((c) => c.id));
    const connectedOnly = activeConnections
      .filter((ac) => ac.status === 'connected' && !savedIds.has(ac.id))
      .map((ac) => ac.connection);
    return [...connections, ...connectedOnly];
  }, [connections, activeConnections]);

  // Engines locked by the existing links. When there are 1+ links every
  // new link must use the same engine (Finding #9). The stored engine on
  // the manifest can be the raw connection engine ('mock' under test
  // fixtures) — normalise both sides through inferEngine so a mock-flagged
  // blueprint still accepts another mock connection.
  const linkedEngines = folder.blueprintMetadata?.linkedDbs ?? [];
  const lockedEngine: UserFacingEngineId | null =
    linkedEngines.length > 0
      ? inferEngine(linkedEngines[0].engine as unknown as string)
      : null;

  useEffect(() => {
    if (!open) return;
    setLabel('');
    setConnectionId('');
    setDatabase('');
    setSubmitting(false);
    setConnecting(false);
    setConnectError(null);
    setLabelError(null);
  }, [open]);

  const selected = allConnections.find((c) => c.id === connectionId);
  const selectedEngine = inferEngine(selected?.engine);
  const engineMismatch =
    !!selected && !canSubmitForEngineMismatch(linkedEngines, selectedEngine);
  const selectedIsConnected = isConnectionActive(selected?.id, activeConnections);

  // Reactive case-insensitive duplicate check — drives both the inline error
  // and the submit-disabled state without requiring a click.
  const labelDuplicate =
    label.trim().length > 0 &&
    existsByLabel(folder.blueprintMetadata?.linkedDbs ?? [], label.trim());

  // Finding #23's list, now read from the SHARED per-connection listing the
  // Object Explorer already fills (`useDatabaseList`): an already-listed
  // connection paints its databases immediately, and a real listing still runs
  // once per open. `databases` is `[]` unless `state === 'ok'`, so the dialog
  // can no longer confuse "listing failed" with "no databases" — the old local
  // `fetchDatabasesForConnection` helper returned `[]` for both.
  const {
    databases,
    state: databasesState,
    isInitialLoading: databasesLoading,
  } = useDatabaseList({
    connectionId,
    engine: selectedEngine,
    enabled: open && !!connectionId && selectedIsConnected,
  });

  // Reset connect-state when the user switches connection.
  useEffect(() => {
    setConnectError(null);
  }, [connectionId]);

  // Finding #23 — populate the DB picker once a connection is chosen AND
  // it is actively connected. While the connection is not established,
  // the dialog renders a "Not connected" panel instead of the picker.
  //
  // D1 fix: depend on SCALAR derivatives of the connection rather than
  // `selected` (the object reference returned by allConnections.find).
  // `allConnections` is rebuilt on every connections-store tick, so
  // `selected` gets a new identity every tick → the effect re-fired →
  // `setDatabase('')` wiped the user's pick mid-dialog.
  //
  // Stable scalars:
  //   • `connectionId`          — the user's chosen connection id (state)
  //   • `selectedEngine`        — derived from the id (string, stable once id is set)
  //   • `selectedIsConnected`   — boolean derived from the active-connections list
  useEffect(() => {
    if (!open || !connectionId || !selectedIsConnected) return;
    setDatabase(getDatabaseFromConnectionId(connectionId, connections, activeConnections) ?? '');
    // `connections`/`activeConnections` deliberately excluded — same D1
    // reasoning as above: they tick on every connection-store update, which
    // would re-fire this effect and wipe the seeded/user-picked database.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, connectionId, selectedEngine, selectedIsConnected]);

  const handleConnect = async () => {
    if (!selected) return;
    setConnecting(true);
    setConnectError(null);
    try {
      const ok = await connectToDatabase(selected as Connection);
      if (!ok) {
        const storeError =
          useConnectionStore.getState().connectionErrorById[selected.id];
        setConnectError(storeError || 'Failed to connect to database');
      }
    } catch (err) {
      setConnectError(
        err instanceof Error ? err.message : 'Failed to connect to database',
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleSubmit = async () => {
    setLabelError(null);
    if (!label.trim()) {
      toast.error({ title: 'Label is required' });
      return;
    }
    if (existsByLabel(
      (folder.blueprintMetadata?.linkedDbs ?? []),
      label.trim(),
    )) {
      setLabelError('Label must be unique within the blueprint (case-insensitive)');
      return;
    }
    if (!selected || !database.trim()) {
      toast.error({ title: 'Select a connection and database' });
      return;
    }
    if (engineMismatch) {
      // Defensive — submit should already be disabled here.
      toast.error({
        title: 'Engine does not match the blueprint',
        description: `This blueprint is ${lockedEngine}. Pick a ${lockedEngine} connection.`,
      });
      return;
    }
    setSubmitting(true);
    try {
      const result = await addBlueprintLink({
        folderPath: folder.directoryPath,
        label: label.trim(),
        connectionId: selected.id,
        connectionDisplayName: safeConnectionName(selected),
        database: database.trim(),
        engine: selectedEngine,
      });
      if (!result.success) {
        toast.error({ title: 'Could not add linked DB', description: result.error });
        return;
      }
      updateBlueprintMetadata(folder.id, {
        linkedDbs: result.metadata.linkedDbs.map((l) => ({
          label: l.label,
          connectionId: l.connectionId,
          connectionDisplayName: l.connectionDisplayName,
          database: l.database,
          engine: l.engine,
          lastSyncAt: l.lastSyncAt,
        })),
      });
      toast.success({
        title: 'Linked DB added',
        description: `Run "Refresh from DB" on ${label.trim()} to populate the baseline.`,
      });
      window.electronAPI?.analytics
        ?.track('feature:database_blueprint_add_link', { engine: selectedEngine })
        .catch(() => {
          /* fire-and-forget */
        });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled =
    submitting ||
    !label.trim() ||
    labelDuplicate ||
    !selected ||
    !database.trim() ||
    engineMismatch ||
    databasesLoading ||
    !selectedIsConnected;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="database-blueprint-add-link-dialog">
        <DialogHeader>
          <DialogTitle>Add linked database</DialogTitle>
          <DialogDescription>
            Track a second (or third) database from the same blueprint folder. The label is
            recorded in the per-connection MetaInfo filename.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="database-blueprint-add-label">Label</Label>
            <Input
              id="database-blueprint-add-label"
              value={label}
              onChange={(e) => { setLabel(e.target.value); setLabelError(null); }}
              placeholder="e.g. Stage, Prod"
              data-testid="database-blueprint-add-label"
            />
            {(labelDuplicate || labelError) && (
              <p
                className="text-xs text-destructive"
                data-testid="database-blueprint-add-label-error"
              >
                {labelError ?? 'Label must be unique within the blueprint (case-insensitive)'}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Connection</Label>
            <Select
              value={connectionId}
              onValueChange={setConnectionId}
            >
              <SelectTrigger data-testid="database-blueprint-add-connection">
                <SelectValue placeholder="Pick a connection…" />
              </SelectTrigger>
              <SelectContent>
                {allConnections.map((c) => {
                  const engine = inferEngine(c.engine);
                  // `lockedEngine` is already normalised by `inferEngine`
                  // above — direct equality here is correct.
                  const incompatible =
                    lockedEngine !== null && engine !== lockedEngine;
                  return (
                    <SelectItem
                      key={c.id}
                      value={c.id}
                      disabled={incompatible}
                      data-testid={`database-blueprint-add-connection-option-${c.id}`}
                    >
                      {safeConnectionName(c)} ({engine})
                      {incompatible ? ' — wrong engine' : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {engineMismatch && (
              <div
                className="flex items-start gap-1.5 text-xs text-destructive"
                data-testid="database-blueprint-engine-mismatch-error"
              >
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  This blueprint is {lockedEngine}. Pick a {lockedEngine} connection so the
                  emitted DDL stays one dialect.
                </span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="database-blueprint-add-database">Database</Label>
            {selected && !engineMismatch && !selectedIsConnected ? (
              <NotConnectedPanel
                connecting={connecting}
                connectError={connectError}
                onConnect={handleConnect}
              />
            ) : (
              <Select
                value={database}
                onValueChange={setDatabase}
                disabled={!selected || engineMismatch || databasesLoading}
              >
                <SelectTrigger
                  id="database-blueprint-add-database"
                  data-testid="database-blueprint-add-database"
                >
                  <SelectValue
                    placeholder={
                      !selected
                        ? 'Select a connection first'
                        : databasesLoading
                          ? 'Loading databases…'
                          : // An empty list because the LISTING FAILED is not the
                            // same claim as "this server has no databases" — the
                            // dropdown body carries the explainer.
                            databasesState === 'error'
                            ? "Couldn't list databases"
                            : databases.length === 0
                              ? 'No databases found'
                              : 'Pick a database…'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <FallbackSelectContent
                    connectionId={connectionId || undefined}
                    state={databasesState}
                    databases={databases}
                    isLoading={databasesLoading}
                    selectedDatabase={database}
                    testIdPrefix="database-blueprint-add"
                    optionTestIdPrefix="database-blueprint-add-database-option"
                  />
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitDisabled}
            data-testid="database-blueprint-add-submit"
          >
            {submitting ? 'Adding…' : 'Add link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type NotConnectedPanelProps = {
  connecting: boolean;
  connectError: string | null;
  onConnect: () => void;
};

/**
 * Inline panel shown in place of the database picker when the picked
 * connection isn't currently active. Lets the user kick off a connect
 * without leaving the dialog.
 */
function NotConnectedPanel({ connecting, connectError, onConnect }: NotConnectedPanelProps) {
  return (
    <div
      className="flex flex-col gap-1.5"
      data-testid="database-blueprint-add-not-connected"
    >
      <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
        <div className="flex items-center gap-2 text-sm">
          {connecting ? (
            <>
              <RandomDotmSpinner size={16} />
              <span data-testid="database-blueprint-add-connecting-label">Connecting…</span>
            </>
          ) : (
            <span>Not connected</span>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={onConnect}
          disabled={connecting}
          data-testid="database-blueprint-add-connect-button"
        >
          Connect
        </Button>
      </div>
      {connectError && !connecting && (
        <div
          className="flex items-start gap-1.5 text-xs text-destructive"
          data-testid="database-blueprint-add-connect-error"
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{connectError}</span>
        </div>
      )}
    </div>
  );
}
